import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { StudentProvider, useStudent } from './context/StudentContext';
import { useAuth } from './context/AuthContext';
import MobileAppNav, { GuestMobileAppNav } from './components/MobileAppNav';
import { hapticMedium } from './hooks/useHaptic';
import { ToastProvider } from './components/ui/toast';
import ErrorBoundary from './components/ui/error-boundary';
import Dashboard from './pages/Dashboard';
import Students from './pages/Students';
import ScoreTracker from './pages/ScoreTracker';
import SchoolExplorer from './pages/SchoolExplorer';
import MajorExplorer from './pages/MajorExplorer';
import Recommendation from './pages/Recommendation';
import VolunteerSim from './pages/VolunteerSim';
import Preferences from './pages/Preferences';
import DataAdmin from './pages/DataAdmin';
import WrongBook from './pages/WrongBook';
import LLMSettings from './pages/LLMSettings';
import AccessibleAIChat from './components/chat/AccessibleAIChat';
import AIChat from './pages/AIChat';
import Login from './pages/Login';
import ChangePasswordModal from './components/auth/ChangePasswordModal';
import EducationLevelBadge from './components/EducationLevelBadge';
import {
  LayoutDashboard, Users, TrendingUp, School, BookOpen,
  Star, ClipboardList, Settings, Database, ChevronDown, Menu, X,
  AlertTriangle, MessageSquare, Lock, LogIn, LogOut, KeyRound, Shield, ChevronUp, Cpu
} from 'lucide-react';

export type PageId = 'dashboard' | 'students' | 'scores' | 'schools' | 'majors' | 'recommendation' | 'volunteer' | 'preferences' | 'llm-settings' | 'data-admin' | 'wrongbook' | 'ai-chat' | 'phase6-summary' | 'login';

// 未登录用户可访问的页面
const PUBLIC_PAGES: PageId[] = ['ai-chat', 'schools', 'majors'];

// 移动端页面导航链 — 支持swipe切换
// 定义每个页面与前后页面的关系，便于swipe导航
const MOBILE_PAGE_SEQUENCE: PageId[] = [
  'dashboard',
  'scores',
  'recommendation',
  'schools',
  'ai-chat',
  'majors',
  'volunteer',
  'wrongbook',
  'students',
  'preferences',
  'llm-settings',
  'data-admin',
];

const navItems: { id: PageId; label: string; icon: any }[] = [
  { id: 'ai-chat', label: 'AI对话', icon: MessageSquare },
  { id: 'dashboard', label: '总览', icon: LayoutDashboard },
  { id: 'scores', label: '成绩轨迹', icon: TrendingUp },
  { id: 'wrongbook', label: '错题本', icon: AlertTriangle },
  { id: 'recommendation', label: '智能推荐', icon: Star },
  { id: 'schools', label: '院校探索', icon: School },
  { id: 'majors', label: '专业探索', icon: BookOpen },
  { id: 'volunteer', label: '志愿模拟', icon: ClipboardList },
  { id: 'students', label: '学生管理', icon: Users },
  { id: 'preferences', label: '偏好设置', icon: Settings },
  { id: 'llm-settings', label: 'AI 设置', icon: Cpu },
  { id: 'data-admin', label: '数据管理', icon: Database },
];

function AppContent() {
  const [currentPage, setCurrentPage] = useState<PageId>('dashboard');
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [showStudentPicker, setShowStudentPicker] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [swipeDirection, setSwipeDirection] = useState<'left' | 'right' | null>(null);
  const { currentStudent, students, setCurrentStudent } = useStudent();
  const { isAuthenticated, mustChangePassword, user, logout, loading: authLoading } = useAuth();
  const mainRef = useRef<HTMLElement>(null);

  // iOS Safari viewport height fix
  useEffect(() => {
    const updateVH = () => {
      document.documentElement.style.setProperty('--vh', `${window.innerHeight * 0.01}px`);
    };
    updateVH();
    window.addEventListener('resize', updateVH);
    return () => window.removeEventListener('resize', updateVH);
  }, []);

  // Scroll to top on page change
  useEffect(() => {
    mainRef.current?.scrollTo(0, 0);
  }, [currentPage]);

  // Auth loading spinner
  if (authLoading) {
    return (
      <div className="flex h-screen items-center justify-center bg-slate-50">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
          <p className="text-gray-500 text-sm">加载中...</p>
        </div>
      </div>
    );
  }

  // 未登录时，如果访问的不是公开页面，跳转到登录
  if (!isAuthenticated && !PUBLIC_PAGES.includes(currentPage)) {
    return <Login />;
  }

  // 未登录但访问公开页面 → 显示精简版
  if (!isAuthenticated) {
    return <GuestLayout currentPage={currentPage} setCurrentPage={setCurrentPage} />;
  }

  // 登录后需要强制改密码
  if (mustChangePassword) {
    return <ChangePasswordModal forced={true} />;
  }

  const renderPage = () => {
    switch (currentPage) {
      case 'ai-chat': return <AIChat onNavigate={(page) => setCurrentPage(page as PageId)} />;
      case 'phase6-summary': return <AccessibleAIChat onNavigate={(page) => setCurrentPage(page as PageId)} />;
      case 'dashboard': return <Dashboard onNavigate={setCurrentPage} />;
      case 'students': return <Students />;
      case 'scores': return <ScoreTracker />;
      case 'wrongbook': return <WrongBook />;
      case 'schools': return <SchoolExplorer />;
      case 'majors': return <MajorExplorer />;
      case 'recommendation': return <Recommendation />;
      case 'volunteer': return <VolunteerSim />;
      case 'preferences': return <Preferences />;
      case 'llm-settings': return <LLMSettings />;
      case 'data-admin': return <DataAdmin />;
      default: return <Dashboard onNavigate={setCurrentPage} />;
    }
  };

  // Swipe navigation handler - detect horizontal drag and navigate
  const handleDragEnd = (event: any, info: any) => {
    // Only on mobile (md breakpoint = 768px)
    if (window.innerWidth >= 768) return;
    
    // Minimum swipe distance to trigger navigation
    const SWIPE_THRESHOLD = 50;
    const velocity = info.velocity.x;
    const distance = info.offset.x;
    
    // Check if swipe distance or velocity is enough
    if (Math.abs(distance) < SWIPE_THRESHOLD && Math.abs(velocity) < 200) {
      return;
    }
    
    const currentIdx = MOBILE_PAGE_SEQUENCE.indexOf(currentPage);
    if (currentIdx === -1) return;
    
    let nextPage: PageId | null = null;
    
    // Swipe right (distance > 0) → go to previous page
    if ((distance > SWIPE_THRESHOLD || velocity > 200) && currentIdx > 0) {
      nextPage = MOBILE_PAGE_SEQUENCE[currentIdx - 1];
    }
    // Swipe left (distance < 0) → go to next page
    else if ((distance < -SWIPE_THRESHOLD || velocity < -200) && currentIdx < MOBILE_PAGE_SEQUENCE.length - 1) {
      nextPage = MOBILE_PAGE_SEQUENCE[currentIdx + 1];
    }
    
    if (nextPage) {
      hapticMedium();
      setSwipeDirection(distance > 0 ? 'right' : 'left');
      setCurrentPage(nextPage);
    }
  };

  return (
    <div className="flex h-screen-safe bg-slate-50 overflow-hidden">
      {/* 侧边栏 — 移动端隐藏 (md: 768px, consistent with AIChat) */}
      <aside className={`${sidebarOpen ? 'w-56' : 'w-16'} flex-shrink-0 bg-gradient-to-b from-blue-900 to-blue-800 text-white hidden md:flex flex-col transition-all duration-300 relative z-10 shadow-xl`}>
        {/* Logo区域 */}
        <div className="flex items-center justify-between px-4 py-5 border-b border-blue-700">
          {sidebarOpen && (
            <div>
              <div className="text-base font-bold tracking-wide">升学规划助手</div>
              <div className="text-blue-300 text-xs mt-0.5">升学规划助手</div>
            </div>
          )}
          <button onClick={() => setSidebarOpen(!sidebarOpen)}
            className="p-1.5 rounded-lg hover:bg-blue-700 transition-colors ml-auto">
            {sidebarOpen ? <X size={16} /> : <Menu size={16} />}
          </button>
        </div>

        {/* 学段标签 */}
        {sidebarOpen && currentStudent && (
          <div className="px-4 pt-3 pb-1">
            <EducationLevelBadge
              educationLevel={currentStudent.education_level}
              size="sm"
            />
          </div>
        )}

        {/* 当前学生切换器 */}
        {currentStudent && (
          <div className="relative mx-3 mt-3">
            <button
              onClick={() => setShowStudentPicker(!showStudentPicker)}
              className="w-full flex items-center gap-2 px-3 py-2 bg-blue-700/50 rounded-xl hover:bg-blue-700 transition-colors"
            >
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center text-sm font-bold text-white flex-shrink-0">
                {currentStudent.name[0]}
              </div>
              {sidebarOpen && (
                <>
                  <div className="flex-1 text-left min-w-0">
                    <div className="text-sm font-medium truncate">{currentStudent.name}</div>
                    <div className="text-blue-300 text-xs truncate">{currentStudent.grade}</div>
                  </div>
                  <ChevronDown size={14} className="flex-shrink-0 text-blue-300" />
                </>
              )}
            </button>

            {/* 学生选择下拉 */}
            {showStudentPicker && sidebarOpen && (
              <div className="absolute top-full left-0 right-0 mt-1 bg-white rounded-xl shadow-xl border border-gray-100 overflow-hidden z-50">
                {students.map(s => (
                  <button
                    key={s.id}
                    onClick={() => { setCurrentStudent(s); setShowStudentPicker(false); }}
                    className={`w-full flex items-center gap-2.5 px-3 py-2.5 hover:bg-blue-50 transition-colors text-left ${currentStudent.id === s.id ? 'bg-blue-50' : ''}`}
                  >
                    <div className="w-7 h-7 rounded-full bg-gradient-to-br from-blue-400 to-indigo-500 flex items-center justify-center text-xs font-bold text-white">
                      {s.name[0]}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5">
                        <span className="text-sm font-medium text-gray-800 truncate">{s.name}</span>
                        <EducationLevelBadge
                          educationLevel={s.education_level}
                          size="sm"
                          className="!py-0 !px-1.5 !text-[10px]"
                        />
                      </div>
                      <div className="text-xs text-gray-500 truncate">{s.grade} · {s.subject_type}</div>
                    </div>
                    {s.is_default === 1 && (
                      <span className="text-xs bg-blue-100 text-blue-600 px-1.5 py-0.5 rounded">默认</span>
                    )}
                  </button>
                ))}
                <button
                  onClick={() => { setCurrentPage('students'); setShowStudentPicker(false); }}
                  className="w-full px-3 py-2.5 text-sm text-blue-600 hover:bg-blue-50 border-t border-gray-100 flex items-center gap-2"
                >
                  <Users size={14} /> 管理学生
                </button>
              </div>
            )}
          </div>
        )}

        {/* 导航菜单 */}
        <nav className="flex-1 px-3 py-4 space-y-0.5 overflow-y-auto">
          {navItems
            .filter(item => {
              // 初中生隐藏：院校探索、专业探索、志愿模拟
              const isJunior = currentStudent?.education_level === 'junior';
              if (isJunior && ['schools', 'majors', 'volunteer'].includes(item.id)) {
                return false;
              }
              return true;
            })
            .map(item => {
            const Icon = item.icon;
            const active = currentPage === item.id;
            return (
              <button
                key={item.id}
                onClick={() => { setCurrentPage(item.id); setShowStudentPicker(false); }}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm transition-all duration-150 ${
                  active
                    ? 'bg-white text-blue-800 shadow-sm font-medium'
                    : 'text-blue-100 hover:bg-blue-700/50 hover:text-white'
                }`}
              >
                <Icon size={18} className="flex-shrink-0" />
                {sidebarOpen && <span className="truncate">{item.label}</span>}
              </button>
            );
          })}
        </nav>

        {/* 底部 */}
        {sidebarOpen && (
          <div className="px-4 pb-4 space-y-3">
            {/* 用户信息 & 菜单 */}
            {isAuthenticated && user && (
              <div className="relative">
                <button
                  onClick={() => setShowUserMenu(!showUserMenu)}
                  className="w-full flex items-center gap-2 text-blue-200 hover:text-white px-2 py-1.5 rounded-lg hover:bg-blue-700/50 transition-colors"
                >
                  <div className="w-7 h-7 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center text-xs font-bold text-white flex-shrink-0">
                    {user.displayName[0]}
                  </div>
                  <span className="text-sm truncate flex-1 text-left">{user.displayName}</span>
                  <ChevronUp size={14} className={`text-blue-300 transition-transform ${showUserMenu ? '' : 'rotate-180'}`} />
                </button>

                {/* 用户菜单弹出 */}
                {showUserMenu && (
                  <div className="absolute bottom-full left-0 right-0 mb-1 bg-white rounded-xl shadow-xl border border-gray-100 overflow-hidden z-50">
                    <button
                      onClick={() => { setShowChangePassword(true); setShowUserMenu(false); }}
                      className="w-full flex items-center gap-2.5 px-3 py-2.5 hover:bg-blue-50 transition-colors text-left text-gray-700 text-sm"
                    >
                      <KeyRound size={14} /> 修改密码
                    </button>
                    <button
                      onClick={() => { setCurrentPage('preferences'); setShowUserMenu(false); }}
                      className="w-full flex items-center gap-2.5 px-3 py-2.5 hover:bg-blue-50 transition-colors text-left text-gray-700 text-sm"
                    >
                      <Shield size={14} /> 偏好设置
                    </button>
                    <button
                      onClick={() => { setCurrentPage('llm-settings'); setShowUserMenu(false); }}
                      className="w-full flex items-center gap-2.5 px-3 py-2.5 hover:bg-blue-50 transition-colors text-left text-gray-700 text-sm"
                    >
                      <Cpu size={14} /> AI 设置
                    </button>
                    <button
                      onClick={() => { logout(); setShowUserMenu(false); }}
                      className="w-full flex items-center gap-2.5 px-3 py-2.5 hover:bg-red-50 transition-colors text-left text-red-600 text-sm border-t border-gray-100"
                    >
                      <LogOut size={14} /> 退出登录
                    </button>
                  </div>
                )}
              </div>
            )}
            <div className="text-blue-400 text-xs">
              <div>v1.0.0 · 升学规划助手</div>
            </div>
          </div>
        )}

      </aside>

      {/* 主内容区 (含移动端头部和底部Tab栏) */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* 移动端顶部导航 */}
        <div className="md:hidden">
          <MobileAppNav
            currentPage={currentPage}
            onNavigate={(page) => { setCurrentPage(page); setShowStudentPicker(false); }}
            studentName={currentStudent?.name}
            userName={user?.displayName}
            onLogout={logout}
            onChangePassword={() => setShowChangePassword(true)}
            hideTabBar={currentPage === 'ai-chat'}
          />
        </div>

        <motion.main
          ref={mainRef}
          drag="x"
          dragElastic={0.15}
          dragConstraints={{ left: 0, right: 0 }}
          dragMomentum={false}
          onDragEnd={handleDragEnd}
          className={`flex-1 overflow-y-auto ${currentPage !== 'ai-chat' ? 'pb-16 md:pb-0' : 'md:pb-0'}`}
          onClick={() => { setShowStudentPicker(false); setShowUserMenu(false); }}
        >
          <AnimatePresence mode="wait" initial={false}>
            <motion.div
              key={currentPage}
              initial={swipeDirection ? {
                opacity: 0,
                x: swipeDirection === 'left' ? 60 : -60,
              } : false}
              animate={{ opacity: 1, x: 0 }}
              exit={swipeDirection ? {
                opacity: 0,
                x: swipeDirection === 'left' ? -60 : 60,
              } : undefined}
              transition={{ duration: 0.2, ease: 'easeOut' }}
              onAnimationComplete={() => setSwipeDirection(null)}
            >
              <ErrorBoundary>
                {renderPage()}
              </ErrorBoundary>
            </motion.div>
          </AnimatePresence>
        </motion.main>
      </div>

      {/* 主动修改密码弹窗（放在最外层，确保移动端也能显示） */}
      {showChangePassword && (
        <ChangePasswordModal forced={false} onClose={() => setShowChangePassword(false)} />
      )}
    </div>
  );
}

/**
 * 游客布局 — 只展示公开页面 (AI对话、院校探索、专业探索)
 */
function GuestLayout({ currentPage, setCurrentPage }: { currentPage: PageId; setCurrentPage: (p: PageId) => void }) {
  const [sidebarOpen, setSidebarOpen] = useState(true);

  const publicNavItems = navItems.filter(item => PUBLIC_PAGES.includes(item.id));
  const protectedNavItems = navItems.filter(item => !PUBLIC_PAGES.includes(item.id));

  const renderPage = () => {
    switch (currentPage) {
      case 'ai-chat': return <AIChat onNavigate={(page) => setCurrentPage(page as PageId)} />;
      case 'schools': return <SchoolExplorer />;
      case 'majors': return <MajorExplorer />;
      default: return <AIChat onNavigate={(page) => setCurrentPage(page as PageId)} />;
    }
  };

  return (
    <div className="flex h-screen-safe bg-slate-50 overflow-hidden">
      <aside className={`${sidebarOpen ? 'w-56' : 'w-16'} flex-shrink-0 bg-gradient-to-b from-blue-900 to-blue-800 text-white hidden md:flex flex-col transition-all duration-300 relative z-10 shadow-xl`}>
        <div className="flex items-center justify-between px-4 py-5 border-b border-blue-700">
          {sidebarOpen && (
            <div>
              <div className="text-base font-bold tracking-wide">升学规划助手</div>
              <div className="text-blue-300 text-xs mt-0.5">智能规划·助力升学</div>
            </div>
          )}
          <button onClick={() => setSidebarOpen(!sidebarOpen)} className="p-1.5 rounded-lg hover:bg-blue-700 transition-colors ml-auto">
            {sidebarOpen ? <X size={16} /> : <Menu size={16} />}
          </button>
        </div>

        <nav className="flex-1 px-3 py-4 space-y-0.5 overflow-y-auto">
          {/* 公开页面 */}
          {publicNavItems.map(item => {
            const Icon = item.icon;
            const active = currentPage === item.id;
            return (
              <button
                key={item.id}
                onClick={() => setCurrentPage(item.id)}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm transition-all duration-150 ${
                  active ? 'bg-white text-blue-800 shadow-sm font-medium' : 'text-blue-100 hover:bg-blue-700/50 hover:text-white'
                }`}
              >
                <Icon size={18} className="flex-shrink-0" />
                {sidebarOpen && <span className="truncate">{item.label}</span>}
              </button>
            );
          })}

          {/* 受保护页面（显示锁图标） */}
          {sidebarOpen && (
            <div className="pt-3 mt-3 border-t border-blue-700/50">
              <p className="px-3 text-xs text-blue-400 mb-2">登录后可用</p>
              {protectedNavItems.map(item => {
                const Icon = item.icon;
                return (
                  <button
                    key={item.id}
                    onClick={() => setCurrentPage('login' as PageId)}
                    className="w-full flex items-center gap-3 px-3 py-2 rounded-xl text-sm text-blue-400/60 cursor-pointer hover:bg-blue-700/30"
                    title="登录后可用"
                  >
                    <Icon size={18} className="flex-shrink-0" />
                    <span className="truncate flex-1">{item.label}</span>
                    <Lock size={12} className="flex-shrink-0" />
                  </button>
                );
              })}
            </div>
          )}
        </nav>

        {sidebarOpen && (
          <div className="px-4 pb-4 space-y-3">
            <button
              onClick={() => setCurrentPage('login' as PageId)}
              className="w-full flex items-center justify-center gap-2 py-2 bg-blue-600 hover:bg-blue-500 rounded-xl text-sm font-medium transition-colors"
            >
              <LogIn size={16} /> 登录
            </button>
            <div className="text-blue-400 text-xs">
              <div>v1.0.0 · 升学规划助手</div>
            </div>
          </div>
        )}
      </aside>

      <div className="flex-1 flex flex-col overflow-hidden">
        {/* 移动端游客导航 */}
        <div className="md:hidden">
          <GuestMobileAppNav
            currentPage={currentPage}
            onNavigate={setCurrentPage}
          />
        </div>

        <main className="flex-1 overflow-y-auto pb-14 md:pb-0">
          <ErrorBoundary>
            {renderPage()}
          </ErrorBoundary>
        </main>
      </div>
    </div>
  );
}

export default function App() {
  return (
    <ToastProvider>
      <StudentProvider>
        <AppContent />
      </StudentProvider>
    </ToastProvider>
  );
}
